import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Brain, Calendar, ShoppingBag, TrendingUp, Leaf, Heart, Star } from 'lucide-react';
import { toast } from 'sonner';
import ReferralSection from '@/components/ReferralSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [doshaResults, setDoshaResults] = useState(null);
  const [wellnessPlan, setWellnessPlan] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState(null);
  const [healthInsights, setHealthInsights] = useState(null);
  const [contentFeed, setContentFeed] = useState([]);
  const [consultationToRate, setConsultationToRate] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingReview, setRatingReview] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    const completed = (consultations || []).filter((c) => c.status === 'completed' && !c.user_rating);
    const mostRecent = completed.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];
    if (mostRecent) setConsultationToRate(mostRecent);
  }, [consultations]);

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeader();
      
      // Fetch Dosha results
      try {
        const doshaRes = await axios.get(`${API}/dosha/results/${user.id}`, { headers });
        setDoshaResults(doshaRes.data);
      } catch (err) {
        // User hasn't taken assessment yet
      }

      // Fetch wellness plan
      try {
        const planRes = await axios.get(`${API}/wellness/plan`, { headers });
        setWellnessPlan(planRes.data);
      } catch (err) {
        // No plan yet
      }

      // Fetch consultations
      const consultationsRes = await axios.get(`${API}/consultations`, { headers });
      setConsultations(consultationsRes.data);

      // Fetch orders
      const ordersRes = await axios.get(`${API}/orders`, { headers });
      setOrders(ordersRes.data);

      // Fetch personalized recommendations (Phase 3)
      try {
        const recRes = await axios.post(`${API}/ai/personalized-recommendations`, {}, { headers });
        setRecommendations(recRes.data);
      } catch (err) {
        console.log('Recommendations not available');
      }

      // Fetch health insights
      try {
        const insightsRes = await axios.get(`${API}/ai/health-insights`, { headers });
        setHealthInsights(insightsRes.data);
      } catch (err) {
        console.log('Insights not available');
      }

      // Fetch personalized content
      try {
        const contentRes = await axios.get(`${API}/ai/content-feed`, { headers });
        setContentFeed(contentRes.data.content || []);
      } catch (err) {
        console.log('Content feed not available');
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getDoshaColor = (dosha) => {
    const colors = {
      vata: 'text-blue-600',
      pitta: 'text-red-600',
      kapha: 'text-green-600',
    };
    return colors[dosha] || 'text-gray-600';
  };

  const handleSubmitRating = async () => {
    if (!consultationToRate || ratingValue < 1) {
      toast.error('Please select a rating (1-5 stars)');
      return;
    }
    setRatingSubmitting(true);
    try {
      await axios.post(
        `${API}/consultations/${consultationToRate.id}/rate`,
        { rating: ratingValue, review: ratingReview || undefined },
        { headers: getAuthHeader() }
      );
      toast.success('Thank you for your rating!');
      setConsultationToRate(null);
      setRatingValue(0);
      setRatingReview('');
      fetchDashboardData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setRatingSubmitting(false);
    }
  };

  const getDoshaPercentage = (prakriti, doshaType) => {
    const total = prakriti.vata + prakriti.pitta + prakriti.kapha;
    return Math.round((prakriti[doshaType] / total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="user-dashboard">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Welcome, {user?.name}
          </h1>
          <p className="text-lg text-gray-600">Your personalized wellness dashboard</p>
        </div>

        {/* Dosha Assessment CTA */}
        {!doshaResults && (
          <Card className="mb-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-none" data-testid="dosha-cta">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-6 md:mb-0">
                  <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    Discover Your Dosha
                  </h2>
                  <p className="text-gray-700">
                    Take our AI-powered assessment to unlock personalized wellness recommendations
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/dosha-assessment')}
                  className="bg-primary hover:bg-primary/90 rounded-full px-8"
                  data-testid="start-assessment-btn"
                >
                  Start Assessment
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Dosha Profile */}
          {doshaResults && (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Brain className="h-5 w-5 text-primary" />
                  <span>Your Dosha Profile</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-2">Primary Dosha</div>
                    <div className={`text-3xl font-bold capitalize ${getDoshaColor(doshaResults.primary_dosha)}`}>
                      {doshaResults.primary_dosha}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {['vata', 'pitta', 'kapha'].map((dosha) => (
                      <div key={dosha}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize">{dosha}</span>
                          <span>{getDoshaPercentage(doshaResults.prakriti, dosha)}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${dosha === 'vata' ? 'bg-blue-500' : dosha === 'pitta' ? 'bg-red-500' : 'bg-green-500'}`}
                            style={{ width: `${getDoshaPercentage(doshaResults.prakriti, dosha)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => navigate('/dosha-assessment')}
                    className="w-full mt-4"
                  >
                    Retake Assessment
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Wellness Plan */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Leaf className="h-5 w-5 text-primary" />
                <span>Today&apos;s Wellness Plan</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {wellnessPlan ? (
                <div className="space-y-4">
                  <div>
                    <div className="font-semibold mb-2">Morning Routine</div>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {wellnessPlan.daily_routine.morning.slice(0, 3).map((item, i) => (
                        <li key={i} className="flex items-start">
                          <span className="mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold mb-2">Recommended Practices</div>
                    <div className="flex flex-wrap gap-2">
                      {wellnessPlan.yoga_practices.slice(0, 3).map((practice, i) => (
                        <span key={i} className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">
                          {practice}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Complete your Dosha assessment to get your personalized wellness plan</p>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-primary" />
                <span>Consultations</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{consultations.length}</div>
              <p className="text-sm text-gray-600">Total bookings</p>
              <Button
                variant="outline"
                onClick={() => navigate('/doctors')}
                className="w-full mt-4"
                data-testid="book-consultation-btn"
              >
                Book Now
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ShoppingBag className="h-5 w-5 text-primary" />
                <span>Orders</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{orders.length}</div>
              <p className="text-sm text-gray-600">Total orders</p>
              <Button
                variant="outline"
                onClick={() => navigate('/products')}
                className="w-full mt-4"
                data-testid="shop-products-btn"
              >
                Shop Now
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Heart className="h-5 w-5 text-primary" />
                <span>Wallet</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">₹{user?.wallet_balance || 0}</div>
              <p className="text-sm text-gray-600">Available balance</p>
              <Button variant="outline" className="w-full mt-4">
                Add Funds
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <span>Progress</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Wellness Score</div>
                  <div className="text-2xl font-bold text-primary">75%</div>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: '75%' }} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personalized Recommendations (Phase 3) */}
          {recommendations && (
            <Card className="md:col-span-2 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Brain className="h-5 w-5 text-primary" />
                  <span>Your Personalized Recommendations</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-3">Recommended Products</h3>
                    <ul className="space-y-2">
                      {recommendations.products?.map((product, i) => (
                        <li key={i} className="flex items-center text-sm">
                          <Leaf className="h-4 w-4 text-primary mr-2" />
                          {product}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-3">Lifestyle Tips</h3>
                    <ul className="space-y-2">
                      {recommendations.lifestyle?.map((tip, i) => (
                        <li key={i} className="flex items-start text-sm">
                          <span className="mr-2">•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-3">Seasonal Advice</h3>
                    <ul className="space-y-2">
                      {recommendations.seasonal_advice?.map((advice, i) => (
                        <li key={i} className="flex items-start text-sm">
                          <span className="mr-2">•</span>
                          {advice}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-xs uppercase tracking-wide text-gray-600 mb-1">Focus Area</div>
                      <div className="text-2xl font-bold text-primary capitalize">
                        {recommendations.health_priority?.replace('_', ' ')}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Personalized Content Feed */}
          {contentFeed.length > 0 && (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Recommended For You</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {contentFeed.map((item) => (
                    <div key={item.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      <div className="h-32 bg-muted overflow-hidden">
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-4">
                        <div className="text-xs text-primary uppercase mb-2">{item.type}</div>
                        <h3 className="font-semibold text-sm mb-2">{item.title}</h3>
                        <div className="text-xs text-gray-600">
                          {item.read_time || item.duration || item.prep_time}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Referral & Loyalty Section */}
          <div className="md:col-span-2">
            <ReferralSection />
          </div>

          {/* Recent Consultations */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Consultations</CardTitle>
            </CardHeader>
            <CardContent>
              {consultations.length > 0 ? (
                <div className="space-y-4">
                  {consultations.slice(0, 3).map((consultation) => (
                    <div key={consultation.id} className="border-b pb-3 last:border-0">
                      <div className="font-medium">Dr. {consultation.doctor_name}</div>
                      <div className="text-sm text-gray-600">
                        {consultation.slot_date} at {consultation.slot_time}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">{consultation.status}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No consultations yet</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Orders */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length > 0 ? (
                <div className="space-y-4">
                  {orders.slice(0, 3).map((order) => (
                    <div key={order.id} className="border-b pb-3 last:border-0">
                      <div className="font-medium">Order #{order.id.slice(0, 8)}</div>
                      <div className="text-sm text-gray-600">₹{order.total_amount}</div>
                      <div className="text-xs text-gray-500 capitalize">{order.status}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No orders yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog open={!!consultationToRate} onOpenChange={(open) => !open && setConsultationToRate(null)}>
        <DialogContent data-testid="rate-consultation-dialog">
          <DialogHeader>
            <DialogTitle>Rate your consultation</DialogTitle>
          </DialogHeader>
          {consultationToRate && (
            <>
              <p className="text-sm text-gray-600">
                How was your consultation with <strong>Dr. {consultationToRate.doctor_name}</strong>?
              </p>
              <div className="space-y-2">
                <Label>Rating (1-5 stars)</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setRatingValue(s)}
                      className="p-1 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <Star className={`h-8 w-8 ${ratingValue >= s ? 'fill-amber-400 text-amber-500' : 'text-gray-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rating-review">Review (optional)</Label>
                <textarea
                  id="rating-review"
                  className="w-full border rounded-lg p-2 min-h-[80px]"
                  placeholder="Share your experience..."
                  value={ratingReview}
                  onChange={(e) => setRatingReview(e.target.value)}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setConsultationToRate(null)}>Skip</Button>
                <Button onClick={handleSubmitRating} disabled={ratingSubmitting || ratingValue < 1}>
                  {ratingSubmitting ? 'Submitting...' : 'Submit rating'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default DashboardPage;